/**
 * DateTimeSelector Component
 *
 * Combined date strip + time slot list for the booking flow.
 * Replaces the separate date (calendar) and time (hero+pills) steps.
 */

import { FC, useState, useRef, useMemo, useCallback, useEffect } from "react"
import {
  View,
  ViewStyle,
  TextStyle,
  Pressable,
  ActivityIndicator,
  useWindowDimensions,
} from "react-native"
import dayjs from "dayjs"
import "dayjs/locale/de"
import Icon from "react-native-vector-icons/MaterialIcons"

import { Text } from "@/components/Text"
import {
  type OpeningHoursResponse,
  type OptimalBookingTimesResponse,
} from "@/services/firebase/functions"
import { colors, spacing, breakpoints } from "@/theme"
import { isDateDisabled, getAvailabilityLabel } from "@/utils/dateTimeUtils"

dayjs.locale("de")

// ─── Props ──────────────────────────────────────────────────────────

export interface DateTimeSelectorProps {
  selectedDate: string
  selectedTime: string
  onDateChange: (date: string) => void
  onTimeSelect: (time: string) => void
  openingHours: OpeningHoursResponse | null | undefined
  optimalTimes: OptimalBookingTimesResponse | null | undefined
  loadingOptimalTimes: boolean
  fetchTimeout: boolean
  serviceType: "pickup_delivery" | "driveby"
  onSwitchToDriveby?: () => void
}

// ─── DateStrip ──────────────────────────────────────────────────────

interface DateChipProps {
  date: dayjs.Dayjs
  isSelected: boolean
  isDisabled: boolean
  onPress: () => void
}

const DateChip: FC<DateChipProps> = ({ date, isSelected, isDisabled, onPress }) => {
  const [isHovered, setIsHovered] = useState(false)

  const chipStyle: ViewStyle[] = [
    $dateChip,
    isDisabled && $dateChipDisabled,
    isSelected && $dateChipSelected,
    !isSelected && !isDisabled && isHovered && $dateChipHovered,
  ].filter(Boolean) as ViewStyle[]

  const textColor = isSelected || (!isDisabled && isHovered) ? colors.surface : colors.text
  const subTextColor =
    isSelected || (!isDisabled && isHovered) ? "rgba(255,255,255,0.8)" : colors.modalHelperText

  return (
    <Pressable
      style={chipStyle}
      onPress={isDisabled ? undefined : onPress}
      onHoverIn={isDisabled ? undefined : () => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      disabled={isDisabled}
    >
      <Text style={[$dateChipDayText, { color: subTextColor }]}>{date.format("dd")}</Text>
      <Text style={[$dateChipMonthText, { color: subTextColor }]}>{date.format("MMM")}</Text>
      <Text style={[$dateChipNumberText, { color: textColor }]}>{date.format("D")}</Text>
    </Pressable>
  )
}

interface DateStripProps {
  selectedDate: string
  onDateChange: (date: string) => void
  openingHours: OpeningHoursResponse | null | undefined
}

const DateStrip: FC<DateStripProps> = ({ selectedDate, onDateChange, openingHours }) => {
  const { width } = useWindowDimensions()
  const chipCount = width < breakpoints.mobile ? 4 : 5
  const [startOffset, setStartOffset] = useState(0)

  const dates = useMemo(() => {
    return Array.from({ length: chipCount }, (_, i) => dayjs().add(startOffset + i, "day"))
  }, [startOffset, chipCount])

  const canGoBack = startOffset > 0

  return (
    <View style={$dateStripContainer}>
      <Pressable
        style={[$dateArrow, !canGoBack && $dateArrowDisabled]}
        onPress={canGoBack ? () => setStartOffset((prev) => Math.max(0, prev - 1)) : undefined}
        disabled={!canGoBack}
      >
        <Icon
          name="chevron-left"
          size={28}
          color={canGoBack ? colors.text : colors.palette.neutral350}
        />
      </Pressable>

      <View style={$dateChipsRow}>
        {dates.map((date) => {
          const dateStr = date.format("YYYY-MM-DD")
          const disabled = isDateDisabled(date, openingHours)
          return (
            <DateChip
              key={dateStr}
              date={date}
              isSelected={dateStr === selectedDate}
              isDisabled={disabled}
              onPress={() => onDateChange(dateStr)}
            />
          )
        })}
      </View>

      <Pressable style={$dateArrow} onPress={() => setStartOffset((prev) => prev + 1)}>
        <Icon name="chevron-right" size={28} color={colors.text} />
      </Pressable>
    </View>
  )
}

// ─── TimeSlotCard ───────────────────────────────────────────────────

interface TimeSlotCardProps {
  time: string
  isSelected: boolean
  isRecommended: boolean
  isFastest: boolean
  availability: { label: string; color: string; icon: string } | null
  onPress: () => void
}

const TimeSlotCard: FC<TimeSlotCardProps> = ({
  time,
  isSelected,
  isRecommended,
  isFastest,
  availability,
  onPress,
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const isHighlighted = isRecommended && !isSelected
  const showHover = !isSelected && !isRecommended && isHovered

  const cardStyle: ViewStyle[] = [
    $timeCard,
    isHighlighted && $timeCardRecommended,
    isSelected && $timeCardSelected,
    showHover && $timeCardHovered,
  ].filter(Boolean) as ViewStyle[]

  const timeColor = isSelected || isHighlighted ? colors.surface : colors.text
  const subtitleColor =
    isSelected || isHighlighted ? "rgba(255,255,255,0.8)" : colors.modalHelperText

  // Build subtitle text
  let subtitle = availability?.label || ""
  if (isRecommended && isFastest) {
    subtitle = "Beste Verfügbarkeit · Frühester Termin"
  } else if (isRecommended) {
    subtitle = "Beste Verfügbarkeit"
  } else if (isFastest) {
    subtitle = subtitle ? `Frühester Termin · ${subtitle}` : "Frühester Termin"
  }

  return (
    <Pressable
      style={cardStyle}
      onPress={onPress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
    >
      <View style={$timeCardLeft}>
        <View style={$timeCardTopRow}>
          <Text style={[$timeCardTimeText, { color: timeColor }]}>{time}</Text>
          {isRecommended && (
            <View style={$badgeRecommended}>
              <Icon name="check" size={10} color={colors.surface} />
              <Text style={$badgeText}>Empfohlen</Text>
            </View>
          )}
          {isFastest && !isRecommended && (
            <View style={$badgeFastest}>
              <Icon name="bolt" size={10} color={colors.surface} />
              <Text style={$badgeText}>Frühester</Text>
            </View>
          )}
        </View>
        {subtitle ? (
          <View style={$timeCardSubtitleRow}>
            {availability && !isSelected && !isHighlighted && (
              <Icon name={availability.icon} size={14} color={availability.color} />
            )}
            <Text style={[$timeCardSubtitle, { color: subtitleColor }]}>{subtitle}</Text>
          </View>
        ) : null}
      </View>
    </Pressable>
  )
}

// ─── TimeSlotList ───────────────────────────────────────────────────

interface TimeSlotListProps {
  selectedDate: string
  selectedTime: string
  onTimeSelect: (time: string) => void
  openingHours: OpeningHoursResponse | null | undefined
  optimalTimes: OptimalBookingTimesResponse | null | undefined
  loadingOptimalTimes: boolean
  fetchTimeout: boolean
  onSwitchToDriveby?: () => void
  onDateChange?: (date: string) => void
}

const TimeSlotList: FC<TimeSlotListProps> = ({
  selectedDate,
  selectedTime,
  onTimeSelect,
  openingHours,
  optimalTimes,
  loadingOptimalTimes,
  fetchTimeout,
  onSwitchToDriveby,
  onDateChange,
}) => {
  // Generate slots from backend data only
  const timeSlots = useMemo(() => {
    if (loadingOptimalTimes) return []
    if (optimalTimes?.allSlots && optimalTimes.allSlots.length > 0 && !fetchTimeout) {
      return optimalTimes.allSlots.map((slot) => slot.time).sort((a, b) => a.localeCompare(b))
    }
    return [] as string[]
  }, [optimalTimes, fetchTimeout, loadingOptimalTimes])

  const recommendedTime = (!loadingOptimalTimes && optimalTimes?.recommendedTime) || null
  const soonestTime = timeSlots.length > 0 ? timeSlots[0] : null

  // Build ordered list: recommended -> fastest -> chronological
  const orderedSlots = useMemo(() => {
    if (timeSlots.length === 0) return []

    const result: string[] = []
    const remaining = [...timeSlots]

    // 1. Recommended first
    if (recommendedTime && remaining.includes(recommendedTime)) {
      result.push(recommendedTime)
      remaining.splice(remaining.indexOf(recommendedTime), 1)
    }

    // 2. Fastest second (if different from recommended)
    if (soonestTime && soonestTime !== recommendedTime && remaining.includes(soonestTime)) {
      result.push(soonestTime)
      remaining.splice(remaining.indexOf(soonestTime), 1)
    }

    // 3. Rest chronologically
    result.push(...remaining)

    return result
  }, [timeSlots, recommendedTime, soonestTime])

  // Find next open day helper
  const findNextOpenDay = useCallback(
    (startDate: string): string => {
      const checkDate = dayjs(startDate)
      for (let i = 1; i <= 7; i++) {
        const candidate = checkDate.add(i, "day")
        if (!isDateDisabled(candidate, openingHours)) {
          return candidate.format("YYYY-MM-DD")
        }
      }
      return checkDate.add(1, "day").format("YYYY-MM-DD")
    },
    [openingHours],
  )

  // Auto-advance tracking — cap consecutive advances at 14 and suppress empty-state flash
  const [advancePending, setAdvancePending] = useState(false)
  const autoAdvancedFromRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    setAdvancePending(false)
  }, [selectedDate])

  useEffect(() => {
    if (!onDateChange) return
    if (loadingOptimalTimes || fetchTimeout) return
    if (!optimalTimes || !selectedDate || !openingHours) return
    // Only act on results that belong to the currently selected date. Some
    // parents (e.g. BookInspectionModal) leave stale optimalTimes in place
    // across a date change, which otherwise pollutes autoAdvancedFromRef.
    if (optimalTimes.metadata?.date !== selectedDate) return
    if (timeSlots.length > 0) return
    if (autoAdvancedFromRef.current.has(selectedDate)) return
    if (autoAdvancedFromRef.current.size >= 14) return

    const nextDay = findNextOpenDay(selectedDate)
    if (!nextDay || nextDay === selectedDate) return

    autoAdvancedFromRef.current.add(selectedDate)
    setAdvancePending(true)

    const timer = setTimeout(() => {
      onDateChange(nextDay)
    }, 300)

    return () => clearTimeout(timer)
  }, [
    timeSlots.length,
    loadingOptimalTimes,
    fetchTimeout,
    optimalTimes,
    selectedDate,
    openingHours,
    onDateChange,
    findNextOpenDay,
  ])

  // Loading state (also covers the brief 300 ms gap before auto-advance fires)
  if (loadingOptimalTimes || advancePending) {
    return (
      <View style={$loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={$loadingText}>
          {advancePending
            ? "Nächsten verfügbaren Tag wird geladen..."
            : "Verfügbare Zeiten werden geladen..."}
        </Text>
      </View>
    )
  }

  // Timeout state
  if (timeSlots.length === 0 && fetchTimeout) {
    return (
      <View style={$emptyContainer}>
        <Icon name="error-outline" size={48} color={colors.amber} />
        <Text style={$emptyTitle}>Termine konnten nicht geladen werden</Text>
        <Text style={$emptyText}>Bitte buche telefonisch oder per WhatsApp.</Text>
      </View>
    )
  }

  // No slots state — only show after a fetch has actually completed (optimalTimes !== null)
  if (timeSlots.length === 0 && !fetchTimeout && optimalTimes !== null) {
    return (
      <View style={$emptyContainer}>
        <Icon name="event-busy" size={48} color={colors.palette.neutral450} />
        <Text style={$emptyTitle}>Keine Termine verfügbar</Text>
        <Text style={$emptyText}>Für diesen Tag sind leider keine Abholzeiten mehr frei.</Text>
        {onSwitchToDriveby && (
          <Pressable style={$drivebyButton} onPress={onSwitchToDriveby}>
            <Icon name="directions-car" size={20} color={colors.surface} />
            <Text style={$drivebyButtonText}>Selbst vorfahren</Text>
          </Pressable>
        )}
        {onDateChange && (
          <Pressable
            style={$nextDayButton}
            onPress={() => onDateChange(findNextOpenDay(selectedDate))}
          >
            <Icon name="calendar-today" size={18} color={colors.modalHelperText} />
            <Text style={$nextDayButtonText}>Zum nächsten Tag</Text>
          </Pressable>
        )}
      </View>
    )
  }

  // Slots available
  return (
    <View>
      {/* Opening hours legend */}
      <View style={$legendRow}>
        <Icon name="schedule" size={14} color={colors.modalHelperText} />
        <Text style={$legendText}>
          Öffnungszeiten: {formatOpeningHours(selectedDate, openingHours)} Uhr
        </Text>
      </View>

      {/* Time slot cards */}
      {orderedSlots.map((time) => (
        <TimeSlotCard
          key={time}
          time={time}
          isSelected={time === selectedTime}
          isRecommended={time === recommendedTime}
          isFastest={time === soonestTime && time !== recommendedTime}
          availability={getAvailabilityLabel(time, optimalTimes)}
          onPress={() => onTimeSelect(time)}
        />
      ))}

      {/* Driveby hint when few slots */}
      {onSwitchToDriveby &&
        optimalTimes &&
        optimalTimes.goodTimes.length + optimalTimes.mediumTimes.length < 10 && (
          <Text style={$drivebyHint}>
            Dein Termin ist nicht dabei aber du hast es eilig? Buche alternativ einen{" "}
            <Text style={$drivebyHintLink} onPress={onSwitchToDriveby}>
              Termin vor Ort.
            </Text>
          </Text>
        )}
    </View>
  )
}

// ─── Opening Hours Formatter ────────────────────────────────────────

function formatOpeningHours(
  selectedDate: string,
  openingHours: OpeningHoursResponse | null | undefined,
): string {
  if (!openingHours?.schedule) return "09:00-17:00"

  const dateObj = dayjs(selectedDate)
  const dateStr = dateObj.format("YYYY-MM-DD")

  if (openingHours.specialDates && openingHours.specialDates[dateStr]) {
    const specialDate = openingHours.specialDates[dateStr]
    if (!specialDate.isOpen) return "Geschlossen"
    return `${specialDate.open}-${specialDate.close}`
  }

  const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]
  const dayName = dayNames[dateObj.day()] as keyof typeof openingHours.schedule
  const daySchedule = openingHours.schedule[dayName]

  if (!daySchedule || !daySchedule.isOpen) return "Geschlossen"
  return `${daySchedule.open}-${daySchedule.close}`
}

// ─── Main Export ────────────────────────────────────────────────────

export const DateTimeSelector: FC<DateTimeSelectorProps> = ({
  selectedDate,
  selectedTime,
  onDateChange,
  onTimeSelect,
  openingHours,
  optimalTimes,
  loadingOptimalTimes,
  fetchTimeout,
  serviceType: _serviceType,
  onSwitchToDriveby,
}) => {
  // Track last known height of the time slot area to prevent layout shift during loading
  const lastSlotHeight = useRef<number>(0)

  return (
    <View style={$container}>
      <DateStrip
        selectedDate={selectedDate}
        onDateChange={onDateChange}
        openingHours={openingHours}
      />

      <View style={$divider} />

      <View
        style={{ minHeight: loadingOptimalTimes ? lastSlotHeight.current : undefined }}
        onLayout={(e) => {
          const h = e.nativeEvent.layout.height
          if (h > 0 && !loadingOptimalTimes) {
            lastSlotHeight.current = h
          }
        }}
      >
        <TimeSlotList
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          onTimeSelect={onTimeSelect}
          openingHours={openingHours}
          optimalTimes={optimalTimes}
          loadingOptimalTimes={loadingOptimalTimes}
          fetchTimeout={fetchTimeout}
          onSwitchToDriveby={onSwitchToDriveby}
          onDateChange={onDateChange}
        />
      </View>
    </View>
  )
}

// ─── Styles ─────────────────────────────────────────────────────────

const $container: ViewStyle = {
  flex: 1,
}

// DateStrip styles
const $dateStripContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  paddingVertical: spacing.sm,
  marginHorizontal: -spacing.md,
}

const $dateArrow: ViewStyle = {
  padding: spacing.xs,
  paddingHorizontal: spacing.md,
  cursor: "pointer" as any,
}

const $dateArrowDisabled: ViewStyle = {
  cursor: "default" as any,
  opacity: 0.4,
}

const $dateChipsRow: ViewStyle = {
  flex: 1,
  flexDirection: "row",
  justifyContent: "center",
  gap: spacing.xs,
}

const $dateChip: ViewStyle = {
  alignItems: "center",
  justifyContent: "center",
  paddingVertical: spacing.sm,
  paddingHorizontal: spacing.md,
  borderRadius: 12,
  borderWidth: 1,
  borderColor: colors.modalBorder,
  backgroundColor: colors.surface,
  minWidth: 68,
  cursor: "pointer" as any,
}

const $dateChipSelected: ViewStyle = {
  backgroundColor: colors.text,
  borderColor: colors.text,
}

const $dateChipDisabled: ViewStyle = {
  opacity: 0.35,
  cursor: "default" as any,
}

const $dateChipHovered: ViewStyle = {
  backgroundColor: colors.primary,
  borderColor: colors.primary,
}

const $dateChipDayText: TextStyle = {
  fontSize: 11,
  fontWeight: "600",
  textTransform: "capitalize",
}

const $dateChipMonthText: TextStyle = {
  fontSize: 11,
  fontWeight: "600",
  marginTop: 2,
  textTransform: "capitalize",
}

const $dateChipNumberText: TextStyle = {
  fontSize: 20,
  fontWeight: "700",
  marginTop: 2,
}

// Divider
const $divider: ViewStyle = {
  height: 1,
  backgroundColor: colors.modalBorder,
  marginVertical: spacing.md,
}

// TimeSlotCard styles
const $timeCard: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  paddingVertical: 16,
  paddingHorizontal: 16,
  borderRadius: 12,
  borderWidth: 1,
  borderColor: colors.modalBorder,
  backgroundColor: colors.surface,
  marginBottom: 8,
  cursor: "pointer" as any,
}

const $timeCardRecommended: ViewStyle = {
  backgroundColor: colors.text,
  borderColor: colors.text,
}

const $timeCardSelected: ViewStyle = {
  backgroundColor: colors.primary,
  borderColor: colors.primary,
}

const $timeCardHovered: ViewStyle = {
  borderColor: colors.primary,
  backgroundColor: "rgba(255, 56, 47, 0.05)",
}

const $timeCardLeft: ViewStyle = {
  flex: 1,
}

const $timeCardTopRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xs,
}

const $timeCardTimeText: TextStyle = {
  fontSize: 18,
  fontWeight: "700",
}

const $timeCardSubtitleRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 4,
  marginTop: 4,
}

const $timeCardSubtitle: TextStyle = {
  fontSize: 13,
  fontWeight: "500",
}

// Badges
const $badgeRecommended: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 3,
  backgroundColor: "rgba(255,255,255,0.2)",
  paddingHorizontal: 8,
  paddingVertical: 3,
  borderRadius: 10,
}

const $badgeFastest: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 3,
  backgroundColor: colors.modalHelperText,
  paddingHorizontal: 8,
  paddingVertical: 3,
  borderRadius: 10,
}

const $badgeText: TextStyle = {
  fontSize: 10,
  fontWeight: "700",
  color: colors.surface,
  letterSpacing: 0.3,
}

// Loading / Empty states
const $loadingContainer: ViewStyle = {
  alignItems: "center",
  justifyContent: "center",
  paddingVertical: spacing.xl,
}

const $loadingText: TextStyle = {
  fontSize: 13,
  color: colors.textDim,
  fontWeight: "500",
  marginTop: spacing.sm,
}

const $emptyContainer: ViewStyle = {
  alignItems: "center",
  paddingVertical: 32,
  gap: 12,
}

const $emptyTitle: TextStyle = {
  fontSize: 18,
  fontWeight: "700",
  color: colors.text,
}

const $emptyText: TextStyle = {
  fontSize: 14,
  color: colors.modalHelperText,
  textAlign: "center",
  paddingHorizontal: 16,
}

const $drivebyButton: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
  backgroundColor: colors.emerald,
  paddingVertical: 12,
  paddingHorizontal: 24,
  borderRadius: 10,
  marginTop: 8,
}

const $drivebyButtonText: TextStyle = {
  color: colors.surface,
  fontSize: 14,
  fontWeight: "600",
}

const $nextDayButton: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 6,
  paddingVertical: 8,
}

const $nextDayButtonText: TextStyle = {
  color: colors.modalHelperText,
  fontSize: 14,
  fontWeight: "500",
}

// Legend
const $legendRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 6,
  marginBottom: spacing.xs,
}

const $legendText: TextStyle = {
  fontSize: 12,
  color: colors.modalHelperText,
}

const _$legendCompact: ViewStyle = {
  flexDirection: "row",
  flexWrap: "wrap",
  gap: spacing.sm,
  marginBottom: spacing.md,
}

const _$legendItem: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 4,
}

const _$legendItemText: TextStyle = {
  fontSize: 11,
  color: colors.modalHelperText,
}

// Driveby hint
const $drivebyHint: TextStyle = {
  fontSize: 12,
  color: colors.textDim,
  lineHeight: 16,
  marginTop: spacing.sm,
  textAlign: "center",
}

const $drivebyHintLink: TextStyle = {
  fontSize: 12,
  lineHeight: 16,
  color: colors.primary,
  fontWeight: "600",
  cursor: "pointer" as any,
}
